import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, MapPin, Clock, Users, RefreshCw } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicDisplayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchReservations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/today-reservations`);
      setData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      // Error handled silently
      setError('Impossible de charger les réservations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReservations();
    
    // Refresh every 2 minutes
    const interval = setInterval(fetchReservations, 120000);
    
    return () => clearInterval(interval);
  }, [fetchReservations]);

  // Enter fullscreen on mount
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          document.documentElement.requestFullscreen();
        }
      }
    };
    
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a8a] to-[#3730a3] flex items-center justify-center">
        <div className="text-white text-center">
          <RefreshCw className="h-16 w-16 animate-spin mx-auto mb-4" />
          <p className="text-2xl">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a8a] to-[#3730a3] flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-2xl">{error}</p>
          <button 
            onClick={fetchReservations}
            className="mt-4 px-6 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const currentTime = format(new Date(), 'HH:mm');
  const formattedDate = data?.date || format(new Date(), "EEEE d MMMM yyyy", { locale: fr });

  // Group reservations by room
  const reservationsByRoom = {};
  data?.reservations?.forEach(res => {
    if (!reservationsByRoom[res.room_name]) {
      reservationsByRoom[res.room_name] = [];
    }
    reservationsByRoom[res.room_name].push(res);
  });

  const roomNames = Object.keys(reservationsByRoom).sort();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#3730a3] text-white p-6 overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img 
            src="/diocese-logo.png" 
            alt="Diocèse de Pamiers" 
            className="h-16 w-auto bg-white rounded-lg p-1"
            onError={(e) => e.target.style.display = 'none'}
          />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Diocèse de Pamiers</h1>
            <p className="text-white/80 text-lg">Planning des salles</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="flex items-center gap-2 text-4xl font-bold">
            <Clock className="h-10 w-10" />
            {currentTime}
          </div>
          <div className="flex items-center gap-2 text-xl capitalize mt-1">
            <Calendar className="h-5 w-5" />
            {formattedDate}
          </div>
        </div>
      </header>

      {/* Main Content */}
      {data?.reservations?.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-20">
            <Calendar className="h-24 w-24 mx-auto mb-6 opacity-50" />
            <p className="text-4xl font-light">Aucune réservation aujourd'hui</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {roomNames.map(roomName => (
            <div key={roomName} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white/20 p-2 rounded-lg">
                  <MapPin className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold">{roomName}</h2>
              </div>
              
              <div className="space-y-3">
                {reservationsByRoom[roomName].map((res, idx) => {
                  // Check if current
                  const now = new Date();
                  const startParts = res.start_time.split(':');
                  const endParts = res.end_time.split(':');
                  const startTime = new Date();
                  startTime.setHours(parseInt(startParts[0]), parseInt(startParts[1]), 0);
                  const endTime = new Date();
                  endTime.setHours(parseInt(endParts[0]), parseInt(endParts[1]), 0);
                  const isCurrent = now >= startTime && now <= endTime;
                  
                  return (
                    <div 
                      key={res.id || idx}
                      className={`
                        p-4 rounded-xl transition-all
                        ${isCurrent 
                          ? 'bg-[#16A34A] shadow-lg shadow-green-500/30 scale-[1.02]' 
                          : 'bg-white/10 hover:bg-white/15'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`
                          text-lg font-bold
                          ${isCurrent ? 'text-white' : 'text-white/90'}
                        `}>
                          {res.start_time} - {res.end_time}
                        </span>
                        {isCurrent && (
                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                            En cours
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 opacity-70" />
                        <span className="text-xl font-medium truncate">
                          {res.display_name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm py-3 px-6">
        <div className="flex items-center justify-between text-sm text-white/60">
          <span>Appuyez sur F pour le plein écran</span>
          <span>
            Dernière mise à jour : {lastUpdate ? format(lastUpdate, 'HH:mm:ss') : '--:--:--'}
          </span>
          <span>{data?.count || 0} réservation(s) aujourd'hui</span>
        </div>
      </footer>
    </div>
  );
}
